<script setup lang="ts">
import { computed } from 'vue'
import { Button } from '@/lib/ui/button'

interface IWebDirectErrorProps {
  readonly type:
    | 'not-found'
    | 'inactive'
    | 'auth-required'
    | 'forbidden'
    | 'error'
  readonly message?: string
  readonly logoUrl?: string
  readonly inline?: boolean
}

interface IWebDirectErrorEmits {
  login: []
  retry: []
}

const props = defineProps<IWebDirectErrorProps>()
const emit = defineEmits<IWebDirectErrorEmits>()

const errorConfig = computed(() => {
  const configs = {
    'not-found': {
      title: 'Chatbot Not Found',
      description:
        'The chatbot you are looking for does not exist or has been removed.',
      showLogin: false,
      showRetry: false,
    },
    inactive: {
      title: 'Chat Paused',
      description:
        'This chat is temporarily paused by the administrator. Please check back later.',
      showLogin: false,
      showRetry: true,
    },
    'auth-required': {
      title: 'Login Required',
      description: 'Please log in to access this chatbot.',
      showLogin: true,
      showRetry: false,
    },
    forbidden: {
      title: 'Access Denied',
      description: "You don't have permission to access this chatbot.",
      showLogin: false,
      showRetry: true,
    },
    error: {
      title: 'Something Went Wrong',
      description:
        props.message || 'An unexpected error occurred. Please try again.',
      showLogin: false,
      showRetry: true,
    },
  }
  return configs[props.type]
})
</script>

<template>
  <div
    class="flex items-center justify-center p-8 bg-background"
    :class="inline ? 'h-full' : 'min-h-dvh'"
  >
    <div class="text-center max-w-sm">
      <img
        v-if="logoUrl && !inline"
        :src="logoUrl"
        alt="Logo"
        class="max-h-12 w-auto mx-auto mb-8"
      />

      <h1
        class="font-semibold text-foreground mb-2"
        :class="inline ? 'text-base' : 'text-xl'"
      >
        {{ errorConfig.title }}
      </h1>

      <p
        class="text-muted-foreground leading-relaxed mb-6"
        :class="inline ? 'text-sm' : 'text-base'"
      >
        {{ errorConfig.description }}
      </p>

      <Button
        v-if="errorConfig.showLogin"
        class="px-7"
        aria-label="Log in to access this chatbot"
        @click="emit('login')"
      >
        Login
      </Button>

      <Button
        v-if="errorConfig.showRetry"
        variant="outline"
        class="px-7 mt-3"
        aria-label="Retry loading chatbot"
        @click="emit('retry')"
      >
        Try Again
      </Button>
    </div>
  </div>
</template>
